/**
 * 转移包组件
 */
'use client';

import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { track } from "@/utils/track";
import { useAtomValue, useSetAtom } from "jotai";
import { transferPackageIsShowAtom, transferPackageShowAtom } from "@/store";
import { DeviceInfo, getDeviceInfo, isInApp } from "@/libs/comeAppSDK";
import TransferPackageModal from "./components/modal";
import dayjs from "dayjs";
import { getTransferPackageInfo } from "./services/api";
import type { TransferPopupConfigRes } from "@/types/api/user/transfer-popup-config";
import { POPUP_SCENE, POPUP_TYPE_MAP, TRANSFER_TYPE_MAP } from "./constants";
import { usePathname, useSearchParams } from "next/navigation";

const cacheKey = 'transfer-package-cache';
// sessionStorage key - 记录本次会话是否已弹窗
const sessionKey = 'transfer-package-entry-shown';
// 轮询时长
const POLLING_DURATION = 5 * 60 * 1000;
// 轮询间隔
const POLLING_INTERVAL = 30000;
// 轮询次数 = 轮询时长 / 轮询间隔
const POLLING_COUNT = POLLING_DURATION / POLLING_INTERVAL;
let hasReport = false;

export default function TransferPackageProvider() {
  const shouldRender = useAtomValue(transferPackageShowAtom);
  const setShouldRender = useSetAtom(transferPackageShowAtom);
  const setIsShow = useSetAtom(transferPackageIsShowAtom);
  // const depositSuccess = useAtomValue(transferPackageDepositAtom);
  const [transferConfig, setTransferConfig] = useState<TransferPopupConfigRes>({});
  const timer = useRef<NodeJS.Timeout | null>(null);
  const deviceInfoRef = useRef<DeviceInfo | null>(null);
  const cacheRef = useRef({});
  const [appId, setAppId] = useState('');
  // 进入app是否弹窗过
  const entryDate = useRef('');
  // 发起后查询10次
  const timeRef = useRef(0);
  const refreshRef = useRef<((delay?: number, needUpdate?: boolean, scenarioType?: number) => void) | null>(null);

  // 获取当前路由信息
  const pathname = usePathname();
  const searchParams = useSearchParams();

  /**
   * 检查是否在允许显示非强制弹窗的路由
   * 允许的路由：首页（/en、/en?menuTab=Explore、/en?menuTab=Casino）
   */
  const isAllowedRouteForNonForce = useCallback(() => {
    // 移除语言前缀，统一处理路由（如 /en/ -> /）
    const pathWithoutLocale = pathname.replace(/^\/(en|pt|hi|id|es|tr|vi)/, '') || '/';

    // 只有在根路径下才允许
    if (pathWithoutLocale !== '/' && pathWithoutLocale !== '') {
      return false;
    }

    // 检查 menuTab 参数
    const menuTab = searchParams?.get('menuTab');

    // 允许：无 menuTab、menuTab=Explore、menuTab=Casino
    return !menuTab || menuTab === 'Explore' || menuTab === 'Casino';
  }, [pathname, searchParams]);

  /**
   * 获取缓存
   */
  const getCache = useCallback((key?: string) => {
    let cache = {};
    try {
      const storage = window.localStorage.getItem(cacheKey) || '{}';
      cache = JSON.parse(storage);
    } catch (e) {
      console.error(e);
    }

    if (key) {
      return cache[key];
    }

    return cache;
  }, []);

  const setCache = useCallback((key: string) => {
    if (!cacheRef.current[key]) {
      cacheRef.current[key] = 0;
    }
    cacheRef.current[key]++;
    window.localStorage.setItem(cacheKey, JSON.stringify(cacheRef.current));
  }, []);

  const fetchPackageInfo = useCallback(async (scenarioType: number) => {
    return new Promise(async (resolve, reject) => {
      try {
        deviceInfoRef.current = deviceInfoRef.current || await getDeviceInfo();
        const configInfo = await getTransferPackageInfo({
          scenarioType,
          appVersion: deviceInfoRef.current.appVersion
        });
        setAppId(deviceInfoRef.current?.appId as string);
        resolve(configInfo);
      } catch (e) {
        console.error(e);
        reject(e);
      }
    });
  }, []);

  refreshRef.current = (delay: number = 5000, needUpdate: boolean = false, scenarioType: number = POPUP_SCENE.ENTRY) => {
    if (timer.current) {
      clearTimeout(timer.current);
    }
    timer.current = setTimeout(async () => {
      try {
        // 如果是进入app场景，先检查本次会话是否已弹窗，如果已弹窗则不发起请求
        if (scenarioType === POPUP_SCENE.ENTRY) {
          try {
            const hasShown = window.sessionStorage.getItem(sessionKey);
            if (hasShown) {
              // 本次会话已经弹过窗，直接返回，不发起请求
              return;
            }
          } catch (e) {
            console.error('sessionStorage error:', e);
          }
        }

        const dateStr = dayjs().format('DD/MM/YYYY');
        const result = await fetchPackageInfo(scenarioType) as TransferPopupConfigRes;
        if (result) {
          const {
            popupInfo = {
              popupType: -1,
              popupCount: 0,
            },
            configId,
            transferType,
            scenarioType,
            needTransfer,
            targetPackageName
          } = result;
          const { popupType, popupCount, } = popupInfo;
          // 触发类型：进入app，当打开app后，只触发一次
          if (dateStr === entryDate.current && scenarioType === POPUP_SCENE.ENTRY) {
            if (needUpdate) {
              timeRef.current++;
              if (timeRef.current < POLLING_COUNT) {
                refreshRef.current?.(POLLING_INTERVAL, true, scenarioType);
              }
            }
            return;
          }

          // 如果弹窗已经打开，直接更新配置（数据同步）
          if (shouldRender && needTransfer) {
            setTransferConfig(result);
            // 继续轮询
            if (needUpdate) {
              timeRef.current++;
              if (timeRef.current < POLLING_COUNT) {
                refreshRef.current?.(POLLING_INTERVAL, true, scenarioType);
              }
            }
            return;
          }

          // 弹窗未打开，检查是否需要显示
          if (!shouldRender && needTransfer) {
            let key = dateStr;
            // 是否强制弹窗
            const isForce = transferType === TRANSFER_TYPE_MAP.FORCE;
            let show = isForce || popupType === POPUP_TYPE_MAP.EACHTIME;
            if (!isForce) {
              // 每日
              if (popupType === POPUP_TYPE_MAP.EVERYDAY) {
                key = `${configId?.toString() as string}_${scenarioType}_${dateStr}`;
                if (!cacheRef.current[key] || cacheRef.current[key] < popupCount) {
                  show = true;
                }
              } else if (popupType === POPUP_TYPE_MAP.FORLIFE) {
                key = `${configId?.toString() as string}_${scenarioType}`;
                if (!cacheRef.current[key] || cacheRef.current[key] < popupCount) {
                  show = true;
                }
              }
            }
            if (show) {
              setTransferConfig(result);
              setCache(key);
              setShouldRender(true);
              if (scenarioType === POPUP_SCENE.ENTRY) {
                entryDate.current = dateStr;
                // 记录本次会话已弹窗
                try {
                  window.sessionStorage.setItem(sessionKey, '1');
                } catch (e) {
                  console.error('sessionStorage error:', e);
                }
              }
              if (!hasReport) {
                track("package_transfer_page_view", {
                  transfer_type: isForce ? 'forced transfer' : 'non-forced transfer',
                  package_name: deviceInfoRef.current?.appId,
                  to_package_name: targetPackageName
                });
              }
              hasReport = true;
            }
          }
        }
      } catch (e) {
        console.error(e);
      }
      if (needUpdate) {
        timeRef.current++;
        if (timeRef.current < POLLING_COUNT) {
          refreshRef.current?.(POLLING_INTERVAL, true, scenarioType);
        }
      }
    }, delay);
  };

  useEffect(() => {
    cacheRef.current = getCache();
  }, []);

  useEffect(() => {
    if (isInApp()) {
      // 立即查询一次 - APP登陆时
      refreshRef.current?.(0, false, POPUP_SCENE.ENTRY);
    }
    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
        timer.current = null;
      }
    };
  }, []);

  // useEffect(() => {
  //   if (depositSuccess) {
  //     timeRef.current = 0;
  //     // 充值完成时
  //     refreshRef.current?.(POLLING_INTERVAL, true, POPUP_SCENE.DEPOSIT);
  //   }
  // }, [depositSuccess]);

  // 判断是否应该渲染弹窗
  const shouldShowModal = () => {
    if (!isInApp() || !shouldRender) {
      return false;
    }

    // 检查配置数据是否有效
    const config = transferConfig as TransferPopupConfigRes;
    const hasValidData = config?.popupInfo?.iconUrl && config?.downloadLink;

    // 如果配置数据无效，不渲染弹窗
    if (!hasValidData) {
      return false;
    }

    // 获取转移类型
    const transferType = config?.transferType;
    const isForce = transferType === TRANSFER_TYPE_MAP.FORCE;

    // 强制转移：任意页面都显示
    if (isForce) {
      setIsShow(true);
      return true;
    }

    // 非强制转移：仅在允许的路由显示
    const isShow = isAllowedRouteForNonForce();
    setIsShow(isShow);
    return isShow;
  };

  if (!shouldShowModal()) {
    return null;
  }

  return createPortal(<TransferPackageModal {...transferConfig} appId={appId} />, document.body);
}